import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import Container from 'reactstrap/lib/Container';
import Button from 'reactstrap/lib/Button';
import { get } from 'lodash-es';
import classnames from 'classnames';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { usePrivacyChoiceTracking } from 'site-modules/shared/hooks/use-privacy-choice-tracking';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { TrackingHandler } from 'client/tracking/handler';
import { TmvMap } from 'client/tracking/maps/tmv';
import { AppraisalTabsMap } from 'client/tracking/maps/appraisal/common/appraisal-tabs';
import { URL_PATHS } from 'site-modules/shared/constants/appraisal/paths';
import { TMV_FAQ } from 'client/site-modules/editorial/constants/faqs';
import { PUB_STATES } from 'client/constants/pub-states';
import { venomHistory } from 'client/utils/history/venom-history';
import { getBaseBreadcrumb, getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { getBuildAndPriceLink } from 'site-modules/shared/utils/build-and-price-link-constructor';
import { getAppraisalLink } from 'site-modules/shared/utils/appraisal-link-constructor';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { InvokeAction } from 'site-modules/shared/components/inventory/invoke-action/invoke-action';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';
import { AppraisalTabsContainer } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tabs-container';
import { WRAPPER_TYPES } from 'site-modules/shared/components/faq/constants';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { VehicleSelectorDrawer } from 'site-modules/shared/components/vehicle-selector/vehicle-selector-drawer';
import { TmvArticles } from 'site-modules/editorial/components/tmv-articles/tmv-articles';
import { BackgroundLinearGradient } from 'site-modules/shared/components/appraisal/background-linear-gradient/background-linear-gradient';
import { isAddressToVinExcludedState } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/is-address-to-vin-excluded-state';

import 'site-modules/shared/components/appraisal/appraisal-common.scss';
import './tmv.scss';

export const BREADCRUMBS = [getHomeBreadcrumb(), getBaseBreadcrumb('True Market Value')];
const CREATIVE_ID = 'tmv_appraisal_drawer';
const TITLE_CLASS_NAME = 'heading-3 mb-0_5';
const BUILD_SELL_CTA_CLASS_NAME = 'd-inline-block mt-0_5 mb-1 size-16 text-primary-darker';
const CONTENT_BLOCK_CLASS_NAMES = 'content-block-rounded bg-white';

export function handleVehicleSelectComplete({ style }) {
  const { make, model, year, displayPubState, pubStates } = style;
  const mmySlugs = { make: make.slug, model: model.slug, year };
  const isStyleStateNew = displayPubState === PUB_STATES.NEW || (pubStates && (pubStates.NEW || pubStates.NEW_USED));

  let destinationLink = getAppraisalLink(mmySlugs);
  if (isStyleStateNew) {
    destinationLink = getBuildAndPriceLink(mmySlugs);
  }

  const eventData = {
    action_name: isStyleStateNew ? TrackingConstant.ACTION_CONFIGURE_VEHICLE : TrackingConstant.ACTION_APPRAISE_VEHICLE,
    subaction_name: TrackingConstant.SUBMIT_YEAR,
    action_category: TrackingConstant.USER_ACTION_CATEGORY,
    action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
    creative_id: CREATIVE_ID,
  };
  if (isStyleStateNew) {
    eventData.value = `${year} - New`;
  } else if (displayPubState === PUB_STATES.USED) {
    eventData.value = `${year} - Used`;
  } else {
    eventData.value = year;
  }

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: eventData,
  });

  venomHistory.push(destinationLink);
}

export function TmvUI({ location = null, isMobile = false, visitorLocation = {} }) {
  const [isVehicleSelectorDrawerOpen, toggleVehicleSelectorDrawerOpen] = useToggle(false);

  useEffect(() => {
    TrackingHandler.useMap({
      ...TmvMap,
      ...AppraisalTabsMap,
    });
  }, []);

  usePrivacyChoiceTracking();

  return (
    <div className="tmv-page pos-r">
      <BackgroundLinearGradient />
      <Container tag="main" id="main-content" tabIndex={-1} className="pb-1">
        <Row>
          <Col xs={12} lg={10}>
            <Breadcrumbs data={BREADCRUMBS} className="ps-0 mb-0_25 text-white" />
            <h1 className="heading-2 text-white">Edmunds TMV&reg; - True Market Value&reg; / Car Values</h1>
            <p className="text-white">Learn more about how to use TMV when shopping for a new car or truck</p>
            <ErrorBoundary>
              <section className={classnames(CONTENT_BLOCK_CLASS_NAMES, 'px-1_5 px-lg-2 pt-1_5 pb-3_5 mt-2_5 mb-1_5')}>
                <h2 className={TITLE_CLASS_NAME}>Appraise Your Car</h2>
                <p>
                  Our free appraisal tool gives you an accurate True Market Value (TMV&reg;) for your car, so that you
                  can get the most when you sell or trade.
                </p>
                <hr className="mt-1_5 mb-2_5 gradient-hr border-0" />
                <AppraisalTabsContainer
                  isMobile={isMobile}
                  isProfileOfferVinPrepopulationEnabled
                  hideEdmundsLogo
                  isSameHeight={false}
                  withContainerPadding={false}
                  isFullWidth
                  isProjectEveStyle
                  renderAddressTab={!isAddressToVinExcludedState(visitorLocation)}
                />
              </section>
            </ErrorBoundary>
            <Row className="mb-2_5">
              <Col xs={12} md={6} className="mb-1 mb-md-0">
                <section className={classnames(CONTENT_BLOCK_CLASS_NAMES, 'py-2 px-1_5')}>
                  <div className="d-flex align-items-center flex-column flex-lg-row">
                    <div>
                      <h2 className={TITLE_CLASS_NAME}>Sell Your Car</h2>
                      <p>
                        Selling your car can be a hassle. Edmunds makes it easy for you with an instant offer in just
                        minutes.
                      </p>
                      <div data-tracking-parent="appraise_build_sell">
                        <a
                          className={BUILD_SELL_CTA_CLASS_NAME}
                          href={URL_PATHS.SELL_CAR_LANDING}
                          data-tracking-id="tool_select_sell_your_car"
                        >
                          <span className="fw-normal">
                            Sell my car <i className="icon-arrow-right4 size-14" aria-hidden />
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </section>
              </Col>
              <Col xs={12} md={6}>
                <section className={classnames(CONTENT_BLOCK_CLASS_NAMES, 'py-2 px-1_5 h-100')}>
                  <div className="d-flex align-items-center align-items-lg-start flex-column flex-lg-row h-100">
                    <div>
                      <h2 className={TITLE_CLASS_NAME}>Build &amp; Price a New Car</h2>
                      <p>Build your car on Edmunds to see the Edmunds True Market Value (TMV&reg;).</p>
                      <div data-tracking-parent="appraise_build_sell" className="mt-auto">
                        <Button
                          onClick={toggleVehicleSelectorDrawerOpen}
                          color="link"
                          className={classnames(
                            BUILD_SELL_CTA_CLASS_NAME,
                            'build-and-price-drawer-btn text-transform-none text-start p-0'
                          )}
                          data-tracking-id="tool_select_build_and_price"
                        >
                          <span className="fw-normal">
                            Build &amp; Price <i className="icon-arrow-right4 size-14" aria-hidden />
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              </Col>
            </Row>
            <div style={isMobile ? undefined : { minHeight: '270px' }}>
              <AdUnit adName="mrect" position={'1'} noRefresh lg xl />
            </div>
            <div className="content-block-opacity mt-2_5 px-1 px-lg-2_5 py-2_5">
              <TmvArticles
                blockClassName="tmv-articles-content-block"
                titleClassName={TITLE_CLASS_NAME}
                withSeparators
              />
              <VehicleSelectorDrawer
                isOpen={isVehicleSelectorDrawerOpen}
                subtitle=""
                onToggle={toggleVehicleSelectorDrawerOpen}
                onSave={handleVehicleSelectComplete}
                finalStepBtnText={{ vehicle: 'Go' }}
                showVehicleSelectOnly
                hasStyle={false}
                trackingParent="tmv-appraisal-drawer"
                pubStates={[PUB_STATES.NEW, PUB_STATES.NEW_USED]}
              />
              <InvokeAction
                href="/appraisal/"
                primaryText="Appraise my car"
                primaryColor="text-primary-darker action-title"
                icon="icon-money10"
                dataTrackingId="view_content"
                trackingDisabled
                classes="d-flex justify-content-between align-items-center px-0_75 px-md-1 mb-0_5 text-decoration-none"
              />
              <Faq
                entries={TMV_FAQ}
                wrapperType={WRAPPER_TYPES.accordion}
                pathname={get(location, 'pathname', '')}
                generateLearnMoreLinks
                isOpenCollapseOnLoad={false}
                visibleEntriesNum={5}
                className="faq bg-white mt-2_5 p-1"
                title="Frequently asked questions"
                accordionAnswerClassName="ms-md-1_5 ps-0_25"
              />
            </div>
          </Col>
        </Row>
        <PageLinksWidget title="Related information" hasTopDivider />
      </Container>
    </div>
  );
}

TmvUI.propTypes = {
  isMobile: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  visitorLocation: PropTypes.shape({}),
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  visitorLocation: state?.visitor?.location,
});

export const Tmv = connect(mapStateToProps)(TmvUI);
